import { useMsal } from '@azure/msal-react';
import LogoCPro from 'assets/logos/logoCPro.svg';
import LogoCProShirt from 'assets/logos/logoCProShirt.svg';
import LogoSherpass from 'assets/logos/logoSherpass.svg';
import LogoSherpassShirt from 'assets/logos/logoSherpassShirt.svg';
import Cookies from 'js-cookie';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { Box, Collapse, Drawer, Link, List, ListItemButton, ListItemIcon, ListItemText, SvgIcon } from '@mui/material';

import { FEATURES_FLAG_KEYS } from '@/features/featuresFlag/apis/featureFlag.keys';
import { useFeaturesFlagState } from '@/features/featuresFlag/hooks';
import { LogOutIcon, TruckIcon, ContactIcon, ChevronRightIcon, InfoIcon, AddIcon } from '@/utils/iconSVG';

import {
  stylesLogo,
  stylesNavDrawer,
  stylesDrawerLinks,
  stylesLogoWrapper,
  stylesNavigationList,
  stylesNavigationIcon,
  stylesNavigationItem,
  stylesNavigationLogout,
  stylesSubNavigationIcon,
  stylesNavigationIconSVG,
  stylesNavigationItemText,
  stylesSecurityPolicyNavigationItem,
} from './NavigationDrawer.styles';

type NavigationDrawerProps = {
  onHoverOpenSideBarDrawer: () => void;
  isSideBarDrawerOpen: boolean;
};

export const NavigationDrawer: React.FunctionComponent<NavigationDrawerProps> = ({
  isSideBarDrawerOpen,
  onHoverOpenSideBarDrawer,
}) => {
  const { t } = useTranslation('layouts');
  const { instance } = useMsal();
  const { pathname } = useLocation();
  const { featuresFlag } = useFeaturesFlagState();
  const [isInformationMenuOpen, setIsInformationMenuOpen] = React.useState(false);

  const isSherpass = featuresFlag.get(FEATURES_FLAG_KEYS.SHERPASS);

  const handleLogout = () => {
    if (isSherpass) {
      instance.logoutRedirect();
    } else {
      Cookies.remove('authToken');
      const win: Window = window;
      win.location = process.env.REACT_APP_PORTAL_URL;
    }
  };

  const handleToggleInformationMenu = () => {
    setIsInformationMenuOpen(!isInformationMenuOpen);
  };

  const isServiceOfferPage = pathname === '/service-offers';
  const isSecurityPolicyPage = pathname === '/security-policy';

  return (
    <Drawer
      variant='permanent'
      open={isSideBarDrawerOpen}
      sx={stylesNavDrawer({ isOpen: isSideBarDrawerOpen, isSherpass })}
      onMouseEnter={onHoverOpenSideBarDrawer}
      onMouseLeave={onHoverOpenSideBarDrawer}
    >
      <Box sx={stylesDrawerLinks}>
        <Box sx={stylesLogoWrapper(isSideBarDrawerOpen)}>
          {isSherpass ? (
            <Box
              component='img'
              alt='CustomsPro'
              sx={stylesLogo}
              src={isSideBarDrawerOpen ? LogoCPro : LogoCProShirt}
            />
          ) : (
            <Box
              component='img'
              alt='Sharepass'
              sx={stylesLogo}
              src={isSideBarDrawerOpen ? LogoSherpass : LogoSherpassShirt}
            />
          )}
        </Box>
        <List component='nav' aria-labelledby='nested-list-subheader' sx={stylesNavigationList}>
          <ListItemButton sx={stylesNavigationItem} component={NavLink} to='/folders'>
            <ListItemIcon sx={stylesNavigationIcon}>
              <SvgIcon component={TruckIcon} sx={stylesNavigationIconSVG} />
            </ListItemIcon>
            <ListItemText primary={t('drawer.folders.title')} sx={stylesNavigationItemText(isSideBarDrawerOpen)} />
          </ListItemButton>
          <ListItemButton
            sx={{ ...stylesNavigationItem, display: !isServiceOfferPage && isSherpass ? 'flex' : 'none' }}
            component={NavLink}
            to='/service-offers'
          >
            <ListItemIcon sx={stylesNavigationIcon}>
              <SvgIcon component={AddIcon} sx={stylesNavigationIconSVG} />
            </ListItemIcon>
            <ListItemText primary={t('drawer.addNewRequest')} sx={stylesNavigationItemText(isSideBarDrawerOpen)} />
          </ListItemButton>

          <ListItemButton
            sx={stylesNavigationItem}
            component={Link}
            href={`${process.env.REACT_APP_ZENDESK_URI}/hc/fr/requests/new`}
            target='_blank'
          >
            <ListItemIcon sx={stylesNavigationIcon}>
              <SvgIcon component={ContactIcon} sx={stylesNavigationIconSVG} />
            </ListItemIcon>
            <ListItemText primary={t('drawer.contactUs')} sx={stylesNavigationItemText(isSideBarDrawerOpen)} />
          </ListItemButton>
          <ListItemButton
            sx={stylesSecurityPolicyNavigationItem(isSecurityPolicyPage)}
            onClick={handleToggleInformationMenu}
          >
            <ListItemIcon sx={stylesNavigationIcon}>
              <SvgIcon component={InfoIcon} sx={stylesNavigationIconSVG} />
            </ListItemIcon>
            <ListItemText primary={t('drawer.information')} sx={stylesNavigationItemText(isSideBarDrawerOpen)} />
          </ListItemButton>
          {isSideBarDrawerOpen && (
            <Collapse in={isInformationMenuOpen} timeout='auto' unmountOnExit>
              <List component='div' disablePadding>
                <ListItemButton sx={stylesNavigationItem} component={NavLink} to='/information/security-policy'>
                  <ListItemIcon sx={stylesSubNavigationIcon}>
                    <SvgIcon component={ChevronRightIcon} sx={stylesNavigationIconSVG} />
                  </ListItemIcon>
                  <ListItemText primary={t('drawer.ResponsibleSecurityPolicy')} />
                </ListItemButton>
                {isSherpass && (
                  <>
                    <ListItemButton sx={stylesNavigationItem} component={NavLink} to='/information/privacy-statement'>
                      <ListItemIcon sx={stylesSubNavigationIcon}>
                        <SvgIcon component={ChevronRightIcon} sx={stylesNavigationIconSVG} />
                      </ListItemIcon>
                      <ListItemText primary={t('drawer.privacyStatement')} />
                    </ListItemButton>
                    <ListItemButton sx={stylesNavigationItem} component={NavLink} to='/information/cookies-list'>
                      <ListItemIcon sx={stylesSubNavigationIcon}>
                        <SvgIcon component={ChevronRightIcon} sx={stylesNavigationIconSVG} />
                      </ListItemIcon>
                      <ListItemText primary={t('drawer.cookiesList')} />
                    </ListItemButton>
                  </>
                )}
                <ListItemButton
                  sx={stylesNavigationItem}
                  component={Link}
                  href={process.env.REACT_APP_FAQ_URI}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  <ListItemIcon sx={stylesSubNavigationIcon}>
                    <SvgIcon component={ChevronRightIcon} sx={stylesNavigationIconSVG} />
                  </ListItemIcon>
                  <ListItemText primary={t('drawer.faq')} />
                </ListItemButton>
              </List>
            </Collapse>
          )}
        </List>
        <List component='nav' aria-labelledby='nested-list-subheader' sx={stylesNavigationLogout}>
          <ListItemButton
            sx={{ ...stylesNavigationItem, justifyContent: isSideBarDrawerOpen ? 'initial' : 'center' }}
            onClick={handleLogout}
          >
            <ListItemIcon sx={stylesNavigationIcon}>
              <SvgIcon component={LogOutIcon} sx={stylesNavigationIconSVG} />
            </ListItemIcon>
            <ListItemText primary={t('drawer.logout')} sx={stylesNavigationItemText(isSideBarDrawerOpen)} />
          </ListItemButton>
        </List>
      </Box>
    </Drawer>
  );
};
